import * as O from 'fp-ts/Option';
import * as TE from 'fp-ts/TaskEither';
import { pipe } from 'fp-ts/lib/function';
import * as Hooks from 'preact/hooks';
import { Api, Fixtuur } from '@fixtuur/backpack';
import * as Logging from '../../store/actions/log';
import * as Core from '../../store/core';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selections from '../../store/selections';
import * as Selectors from '../../store/selectors';
import { getConfiguration } from '../shared/getConfiguration';
import { buildConfigurationFallbacks } from '../shared/useConfigurationFallbacks';
import { useGetConfigurationUnlockedOptions } from '../shared/useGetOptions/useGetConfigurationOptions';

/**
 * Initialises selections for configuration once fallbacks have loaded
 *
 * This would ideally be done as part of configuration initialising, but at the moment that's tricky
 * because it depends on configuration options having loaded in order to fetch fallbacks and all
 * that code is still happening reactively.
 *
 * Hence, this needs to happen reactively unless we change the above.
 */
export const useInitSelectionsForConfiguration = () => {
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const inConfiguration = useSelector(Selectors.Initialisation.inConfiguration);
    const currentConfigurator = useSelector(Selectors.Initialisation.currentConfiguratorDefinition);
    const configurationStartingProductGroupingCode = useSelector(
        Selectors.Initialisation.configurationStartingProductGroupingCode,
    );
    const startingProductId = useSelector(Selectors.Initialisation.configurationStartingProductId);
    const startingConfigurationSelections = useSelector(
        Selectors.Initialisation.startingSelections,
    );
    const dispatch = useDispatch();

    const allConfigSpaceOptions = useGetConfigurationUnlockedOptions();

    // Initialise selections state if not already?
    Hooks.useEffect(() => {
        if (!serviceConfig || !allConfigSpaceOptions || !inConfiguration || !currentConfigurator || (!startingConfigurationSelections && !startingProductId)) {
            return;
        }

        const fallbacks = buildConfigurationFallbacks(currentConfigurator, allConfigSpaceOptions);

        /**
         * Find the proposal we want to prioritise (i.e. the one that contains our inspiration starting product) and bring it to the front.
         **/
        const apiFallbacks = fallbacks.map(Fixtuur.Configuration.Selections.toApi);
        const startingSelections = pipe(
            startingProductId,
            O.fromNullable,
            O.chain(id =>
                O.fromNullable(
                    apiFallbacks.find(selections => Object.values(selections)[0] === id),
                ),
            ),
            O.map(Fixtuur.Configuration.Selections.create),
            O.alt(() => O.fromNullable(startingConfigurationSelections)),
            O.toNullable,
        );

        if (!startingSelections) {
            /**
             * There are 3 cases where we wouldn't be able to lock a starting selection:
             * 1. No starting selection in the first place, e.g. launching directly into config. - valid state.
             * 2. No starting selection but there should be - undetectable.
             * 3. Starting selection exists but can't be found in proposals - indicates we are out of sync, log warning.
             */
            dispatch(
                Logging.logWarning(
                    'Unable to lock starting selection: could not find starting product in proposals.',
                ),
            );
            /**
             * TODO: this error occurs on the first time and resolves after that, so gives a false warning.
             * Look into the loading states (isFallbacksLoading, isAllOptionsLoading, isAllConfigurationOptionsLoading)
             * and see if there's a fault there.
             * It looks like we're receiving two sets of fallbacks: the first one being a subset of the final set, like we're getting them too early...
             */
        }

        const proposals = [
            startingSelections !== null
                ? Fixtuur.Configuration.Selections.toApi(startingSelections)
                : {},
            ...apiFallbacks,
        ];

        pipe(
            TE.Do,
            TE.bind('response', () =>
                pipe(
                    // resolve proposals to a set of selections
                    Api.Configuration.Selections.fromProposal(
                        serviceConfig,
                        currentConfigurator.configurationSpaceId,
                        proposals,
                    ),
                    TE.mapLeft(error =>
                        dispatch(
                            Core.error({
                                message: error.message,
                                context: {
                                    type: 'render',
                                },
                            }),
                        ),
                    ),
                ),
            ),
            TE.bind('options', ({ response }) =>
                TE.right(Fixtuur.Configuration.Options.fromApi(response.options)),
            ),
            TE.bind('selections', ({ response }) =>
                TE.right(Fixtuur.Configuration.Selections.create(response.selections)),
            ),
            TE.map(({ options, selections }) => {
                try {
                    const productSelectionsByPart =
                        Fixtuur.Configuration.ProductSelectionsByPart.fromConfiguratorAndSelections(
                            currentConfigurator,
                            selections,
                        );

                    /**
                     * Use the 'options' part of the /selections response to find the property key
                     * use this key (e.g. 'Basin:GroupingCode') to form grouping code selections.
                     *
                     * when flag is off, grouping code selections is empty.
                     *
                     * TODO:
                     * perhaps log specific error case where flag is on but grouping code is undefined
                     */
                    const groupingCodeSelections = pipe(
                        O.Do,
                        O.bind('groupingCode', () =>
                            O.fromNullable(configurationStartingProductGroupingCode),
                        ),
                        O.bind('key', ({ groupingCode }) =>
                            Fixtuur.Configuration.Options.nameByValue(groupingCode, options),
                        ),
                        O.map(({ key, groupingCode }) =>
                            Fixtuur.Configuration.Selections.create({ [key]: groupingCode }),
                        ),
                        // if there was some error, return empty and log a warning
                        O.getOrElse(() => {
                            dispatch(Logging.logWarning('Unable to apply root product lock.'));
                            return Fixtuur.Configuration.Selections.empty();
                        }),
                    );

                    getConfiguration(
                        serviceConfig,
                        currentConfigurator.configurationSpaceId,
                        selections,
                        dispatch,
                    )
                        .then(currentConfiguration => {
                            if (currentConfiguration) {
                                // Find the hero part based on starting product ID, otherwise default to the first one in the configuration
                                const heroPartsFromConfiguration =
                                    Fixtuur.Configuration.heroParts(currentConfiguration);
                                let heroPart = heroPartsFromConfiguration[0];
                                if (startingProductId) {
                                    heroPartsFromConfiguration.forEach(hp => {
                                        const productProperty =
                                            currentConfigurator.partsMap[hp].productProperty;
                                        const startingProductIsHero =
                                            Fixtuur.Configuration.Options.isOptionIncluded(
                                                options,
                                                productProperty,
                                                startingProductId,
                                            );
                                        if (startingProductIsHero) {
                                            heroPart = hp;
                                        }
                                    });
                                }
                                const buildPropertySelections =
                                    Fixtuur.Configuration.Selections.getBuildPropertySelectionsForConfiguration(
                                        currentConfigurator,
                                        selections,
                                        heroPart,
                                    );
                                dispatch(
                                    Selections.initialise({
                                        productSelectionsByPart,
                                        groupingCodeSelections,
                                        buildPropertySelections,
                                        attachmentProposals: null,
                                        attachmentSelections: null,
                                    }),
                                );
                            } else {
                                throw new Error(
                                    `No valid configuration could be found in useInitSelectionsForConfiguration. spaceId: ${
                                        currentConfigurator.configurationSpaceId
                                    } \n selections: ${JSON.stringify(selections)}`,
                                );
                            }
                        })
                        .catch(err =>
                            dispatch(
                                Core.error({
                                    message:
                                        (err as any)?.message ??
                                        'Failed to get product selections by part - this may be an out of sync error',
                                    context: {
                                        type: 'log',
                                    },
                                }),
                            ),
                        ); // this catch specifically for vitest which can't seem to track that this error path is handled
                } catch (err) {
                    // TODO: this errors on the first time and resolves after that, just logging the error for now but should look into
                    dispatch(
                        Core.error({
                            message:
                                (err as any)?.message ??
                                'Failed to get product selections by part - this may be an out of sync error',
                            context: {
                                type: 'log',
                            },
                        }),
                    );
                }
            }),
        )();
    }, [
        serviceConfig,
        startingConfigurationSelections,
        startingProductId,
        allConfigSpaceOptions,
        currentConfigurator,
        inConfiguration,
    ]);
};
